import useSessionStorage from "@/hooks/use-session-storage"
import { AccessToken } from "@/models/account"
import { useNavigate } from "react-router"
import React, { createContext, useContext, useCallback } from "react"

// Impersonated user info stored in session
export interface ImpersonatedUser {
	id: string
	email: string
	name: string
}

// Impersonation state stored in session
export interface ImpersonationState {
	originalToken: AccessToken
	impersonatedUser: ImpersonatedUser
}

export const AccessContext = createContext<{
	accessToken?: AccessToken
	setAccessToken: (accessToken?: AccessToken) => void
	logout: () => void
	requiresAuth: () => boolean
	// Impersonation methods
	impersonationState?: ImpersonationState
	startImpersonation: (tokens: AccessToken, impersonatedUser: ImpersonatedUser) => void
	stopImpersonation: () => void
	isImpersonating: boolean
}>({
	setAccessToken: () => null,
	logout: () => undefined,
	requiresAuth: () => false,
	// Impersonation defaults
	startImpersonation: () => undefined,
	stopImpersonation: () => undefined,
	isImpersonating: false,
})

export const useAccess = () => useContext(AccessContext)

const ACCESS_TOKEN = "ACCESS_TOKEN"
const IMPERSONATION_STATE = "IMPERSONATION_STATE"

export const getUserToken = () =>
	JSON.parse(window.sessionStorage.getItem(ACCESS_TOKEN)!)?.access_token

export const useAccessToken = () =>
	useSessionStorage<AccessToken | undefined>(ACCESS_TOKEN)

export const useImpersonationState = () =>
	useSessionStorage<ImpersonationState | undefined>(IMPERSONATION_STATE)

export const AccessProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const navigate = useNavigate()
	const [accessToken, setAccessToken] = useAccessToken()
	const [impersonationState, setImpersonationState] = useImpersonationState()

	const goToLogin = () => navigate("/account/login", { replace: true })

	const logout = useCallback(() => {
		// Also clear impersonation state on logout
		setImpersonationState(undefined)
		setAccessToken(undefined)
		goToLogin()
	}, [setAccessToken, setImpersonationState])

	const requiresAuth = () => {
		const accessToken = getUserToken()
		const isAuthenticated = accessToken !== undefined

		if (!isAuthenticated && typeof window !== "undefined") {
			goToLogin()
		}

		return isAuthenticated
	}

	/**
	 * Start impersonating another user.
	 * Stores the original admin token and switches to the impersonated user's tokens.
	 */
	const startImpersonation = useCallback(
		(tokens: AccessToken, impersonatedUser: ImpersonatedUser) => {
			if (!accessToken) return

			// Store the original token and impersonated user info
			setImpersonationState({
				originalToken: accessToken,
				impersonatedUser,
			})

			// Switch to the impersonated user's tokens
			setAccessToken(tokens)
		},
		[accessToken, setAccessToken, setImpersonationState]
	)

	/**
	 * Stop impersonating and restore the original admin token.
	 */
	const stopImpersonation = useCallback(() => {
		if (!impersonationState) return

		// Restore the original admin token
		setAccessToken(impersonationState.originalToken)

		// Clear impersonation state
		setImpersonationState(undefined)
	}, [impersonationState, setAccessToken, setImpersonationState])

	const isImpersonating = impersonationState !== undefined

	return (
		<AccessContext.Provider
			value={{
				accessToken,
				setAccessToken,
				logout,
				requiresAuth,
				// Impersonation
				impersonationState,
				startImpersonation,
				stopImpersonation,
				isImpersonating,
			}}
		>
			{children}
		</AccessContext.Provider>
	)
}
